import { Button, Textarea } from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TravelRequestConfirmActionButton } from '@/module/Travel/Request/components/TravelRequestConfirmActionButton';
import {
  TravelApprovalStep,
  TravelRequest,
  currentTravelApprovalStep,
  hasCurrentTravelBoardDecision,
  isTravelPooledStep,
  travelStepFamily,
} from '@/module/Travel/Request/model/TravelRequestTypes';

import { CurrentUser, RoleType, useCurrentUser } from '@feature/security';

import {
  useTravelApprovalApprove,
  useTravelApprovalBoardSent,
  useTravelApprovalReject,
  useTravelApprovalReturn,
} from '../model/TravelApprovalQuery';
import { TravelApprovalReasonAction, TravelApprovalReasonModal } from './TravelApprovalReasonModal';
import { TravelApprovalReturnModal } from './TravelApprovalReturnModal';
import { TravelBoardDecisionModal } from './TravelBoardDecisionModal';

interface TravelApprovalActionsProps {
  request: TravelRequest;
  board?: boolean;
}

type TravelApprovalDialog = Extract<TravelApprovalReasonAction, 'return' | 'reject'> | 'boardDecision';

export function decidableTravelApprovalStep(request: TravelRequest, user: CurrentUser): TravelApprovalStep | null {
  if (request.status !== 'SUBMITTED') {
    return null;
  }

  const step = currentTravelApprovalStep(request.approvalSteps);
  if (step == null) {
    return null;
  }
  // A pending dean step stays named for the dean, and the backend checks the vice-dean's faculty.
  if (
    step.stepKey === 'DEAN' &&
    step.decision === 'PENDING' &&
    user.currentRole === RoleType.VICE_DEAN &&
    request.requester.id !== user.id
  ) {
    return step;
  }
  if (step.roleType !== user.currentRole) {
    return null;
  }

  // Every step that is not pooled needs its named approver.
  const pooled = isTravelPooledStep(step) && step.approver == null;
  return pooled || step.approver?.id === user.id ? step : null;
}

// With `board` set, the instance decides only the board step (true) or only the steps before it (false).
export function TravelApprovalActions({ request, board }: TravelApprovalActionsProps) {
  const stepId = currentTravelApprovalStep(request.approvalSteps)?.id;

  // Keyed so a typed note or open dialog never carries over to another request or step.
  return (
    <TravelApprovalActionsContent
      key={`${request.id}:${stepId}`}
      request={request}
      board={board}
    />
  );
}

function TravelApprovalActionsContent({ request, board }: TravelApprovalActionsProps) {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const [reasonAction, setReasonAction] = useState<TravelApprovalDialog | null>(null);
  const [note, setNote] = useState('');

  const approve = useTravelApprovalApprove(request.id);
  const returnForChanges = useTravelApprovalReturn(request.id);
  const reject = useTravelApprovalReject(request.id);
  const boardSent = useTravelApprovalBoardSent(request.id);

  const step = decidableTravelApprovalStep(request, currentUser);
  if (step == null || (board != null && board !== (step.stepKey === 'ADMINISTRATIVE_BOARD'))) {
    return null;
  }

  // The board decision needs its file, so one dialog takes the file and the outcome together.
  const boardStep = step.stepKey === 'ADMINISTRATIVE_BOARD';
  // The secretariat verifies or returns; the backend refuses a reject on its step.
  const verification = step.stepKey === 'SECRETARIAT_VERIFICATION';
  const family = `travel.stepFamily.${travelStepFamily(step)}`;
  // The verifying office decides which step comes next, so its confirm text is per office.
  const verifyingOffice =
    step.roleType === RoleType.INTERNATIONAL_ADMINISTRATIVE_OFFICE
      ? RoleType.INTERNATIONAL_ADMINISTRATIVE_OFFICE
      : RoleType.RESEARCH_ADMINISTRATIVE_OFFICE;
  const closeReason = () => setReasonAction(null);
  const trimmedNote = note.trim();

  return (
    <>
      {boardStep && step.sentToBoardAt == null && !hasCurrentTravelBoardDecision(request) && (
        <TravelRequestConfirmActionButton
          label={t('travel.approvals.boardSent.action')}
          confirmTitle={t('travel.approvals.boardSent.confirmTitle')}
          confirmContent={t('travel.approvals.boardSent.confirmContent')}
          variant="outline"
          mutation={boardSent}
        />
      )}
      {boardStep ? (
        <Button
          size="xs"
          onClick={() => setReasonAction('boardDecision')}
        >
          {t('travel.approvals.boardDecision.action')}
        </Button>
      ) : (
        <TravelRequestConfirmActionButton
          label={t(`${family}.action`)}
          confirmTitle={t(`${family}.confirmTitle`)}
          confirmContent={t(verification ? `${family}.confirmContent.${verifyingOffice}` : `${family}.confirmContent`)}
          mutation={approve}
          variables={trimmedNote ? { reason: trimmedNote } : undefined}
        >
          <Textarea
            label={t('travel.approvals.approvalNote')}
            value={note}
            onChange={event => setNote(event.currentTarget.value)}
            disabled={approve.isPending}
            autosize
            minRows={3}
          />
        </TravelRequestConfirmActionButton>
      )}
      <Button
        size="xs"
        color="yellow.4"
        onClick={() => setReasonAction('return')}
      >
        {t(verification ? 'travel.approvals.actions.verifyReturn' : 'travel.approvals.actions.return')}
      </Button>
      {!verification && !boardStep && (
        <Button
          size="xs"
          color="red"
          onClick={() => setReasonAction('reject')}
        >
          {t(`${family}.rejectAction`)}
        </Button>
      )}
      {reasonAction === 'boardDecision' && (
        <TravelBoardDecisionModal
          request={request}
          onClose={closeReason}
        />
      )}
      {reasonAction === 'return' && (
        <TravelApprovalReturnModal
          title={t('travel.approvals.actions.confirmReturn.title')}
          content={t('travel.approvals.actions.confirmReturn.content')}
          mutation={returnForChanges}
          onClose={closeReason}
        />
      )}
      {reasonAction === 'reject' && (
        <TravelApprovalReasonModal
          action="reject"
          title={t('travel.approvals.actions.confirmReject.title')}
          content={t('travel.approvals.actions.confirmReject.content')}
          submitLabel={t(`${family}.rejectAction`)}
          mutation={reject}
          onClose={closeReason}
        />
      )}
    </>
  );
}
